import React from 'react'
import axios from 'axios'
import maplibregl from 'maplibre-gl'
import { useGlobalState } from 'state'
import { IDs } from 'common/consts'
import dayjs from 'dayjs'
import { RoadSymbols } from 'common/consts'
import { RoadType } from 'common/types'


const roadColors = {
  Jizen: {
    category: '事前',
    color: '#0000FF',
    label: '事前規制'
  },
  Saigai: {
    category: '災害等',
    color: '#FB0006',
    label: '災害規制'
  },
  Kisyou: {
    category: '気象条件',
    color: '#65F2F4',
    label: '気象規制'
  },
  Kouji: {
    category: '工事',
    color: '#E5CF0A',
    label: '工事規制'
  },
  Yokoku: {
    category: '予告',
    color: '#118901',
    label: '予告規制'
  },
  Etc: {
    category: 'その他',
    color: '#8FA8A9',
    label: 'その他規制'
  },
  Event: {
    category: 'イベント',
    color: '#F38336',
    label: 'イベント規制'
  }
}

export const RoadRegulationLayer = () => {
  const state = useGlobalState()
  const sourceId = IDs.roadRegulation.source
  const layerId = IDs.roadRegulation.layer
  const outlineLayoutId = `${layerId}_outline`
  const lineLayoutId = `${layerId}_line`
  const symbolLayoutId = `${layerId}_symbol`
  const [features, setFeatures] = React.useState<GeoJSON.Feature[]>([])

  const startDate = React.useMemo(() => state.app.baseDate.add(9, 'hours').format('YYYY-MM-DD HH:mm:59'), [state.app.baseDate])

  React.useEffect(() => {
    if (!state.mapbox) return

    load()

    state.mapbox?.on('mouseenter', symbolLayoutId, mouseEnterListener)
    state.mapbox?.on('mouseleave', symbolLayoutId, mouseLeaveListener)
    state.mapbox?.on('click', symbolLayoutId, clickListener)
    state.mapbox?.on('style.load', load)
    return () => {
      state.mapbox?.off('mouseenter', symbolLayoutId, mouseEnterListener)
      state.mapbox?.off('mouseleave', symbolLayoutId, mouseLeaveListener)
      state.mapbox?.on('click', symbolLayoutId, clickListener)
      state.mapbox?.off('style.load', load)
    }
  }, [state.mapbox])

  const mouseEnterListener = () => {
    if (!state.mapbox) return
    state.mapbox.getCanvas().style.cursor = 'pointer'
  }

  const mouseLeaveListener = () => {
    if (!state.mapbox) return
    state.mapbox.getCanvas().style.cursor = ''
  }

  const getLineCenter = (lineCoordinates: [number, number][]): [number, number] | null => {
    let totalDistance = 0
    const cumulativeDistance: number[] = [0]

    // 各座標の累積距離を計算
    for (let i = 1; i < lineCoordinates.length; i++) {
      const distance = Math.sqrt(
        Math.pow(lineCoordinates[i][0] - lineCoordinates[i - 1][0], 2) +
        Math.pow(lineCoordinates[i][1] - lineCoordinates[i - 1][1], 2)
      )
      totalDistance += distance
      cumulativeDistance.push(totalDistance)
    }

    // 中心までの距離
    const halfDistance = totalDistance / 2

    // 中心までの距離に達する座標を見つける
    let centerCoord: [number, number] | null = null
    for (let j = 1; j < lineCoordinates.length; j++) {
      if (cumulativeDistance[j] >= halfDistance) {
        const ratio = (halfDistance - cumulativeDistance[j - 1]) / (cumulativeDistance[j] - cumulativeDistance[j - 1])
        centerCoord = [
          lineCoordinates[j - 1][0] + ratio * (lineCoordinates[j][0] - lineCoordinates[j - 1][0]),
          lineCoordinates[j - 1][1] + ratio * (lineCoordinates[j][1] - lineCoordinates[j - 1][1])
        ]
        break
      }
    }
    return centerCoord
  }

  const clickListener = (e: any) => {
    if (!state.mapbox) return

    const coordinates = e.features[0].geometry.coordinates

    // 'line-center'の場合、線の中心座標を取得
    const center = getLineCenter(coordinates)
    if (!center) return

    const properties = e.features[0].properties
    const startDate = dayjs(properties.start_date).format('YYYY/MM/DD HH:mm')
    const endDate = !properties.end_date ? '未設定' : dayjs(properties.end_date).format('YYYY/MM/DD')
    let address = properties.start_address
    if (address !== properties.end_address) {
      address += '〜' + properties.end_address
    }
    const publicMemo = !properties.public_memo ? '' : `<div class="memo">${properties.public_memo}</div>`
    const html = `<div class="road-regulation">
      <div class="row"><div class="title">種別</div><div>${properties.regulation_type}</div></div>
      <div class="row"><div class="title">原因</div><div> ${properties.reason} ${properties.reason_detail}</div></div>
      <div class="row"><div class="title">期間</div><div> ${startDate} 〜 ${endDate}</div></div>
      <div class="row"><div class="title">路線</div><div> ${properties.road_name}</div></div>
      <div class="row"><div class="title">区間</div><div> ${address}</div></div>
      ${publicMemo}
    </div>`

    const zoom = e.target.getZoom()
    state.map.setView(center[0], center[1], zoom > 12 ? zoom : 12)

    new maplibregl.Popup()
      .setLngLat(center)
      .setHTML(html)
      .setOffset([0, -10])
      .addTo(state.mapbox)
  }

  React.useEffect(() => {
    fetchFeatures()
  }, [startDate])

  React.useEffect(() => {
    if (!state.mapbox) return
    setLayoutvisibility()
  }, [state.app.roadRegulation])

  const setLayoutvisibility = () => {
    if (!state.mapbox) return
    state.mapbox.setLayoutProperty(
      outlineLayoutId,
      'visibility',
      state.app.roadRegulation ? 'visible' : 'none'
    )
    state.mapbox.setLayoutProperty(
      lineLayoutId,
      'visibility',
      state.app.roadRegulation ? 'visible' : 'none'
    )
    state.mapbox.setLayoutProperty(
      symbolLayoutId,
      'visibility',
      state.app.roadRegulation ? 'visible' : 'none'
    )
  }

  React.useEffect(() => {
    if (!state.map) return
    if (!state.map.hasSource(sourceId)) return
    state.map.updateSourceData(sourceId, getFilterFeatureCollection())
  }, [state.app.roadRegulationTypes, features])

  const fetchData = async (options: {page: number}) => {
    const row = 100
    const baseUrl = 'https://openapi.city.shizuoka.jp/opendataapi/servicepoint/roadRegulation'
    const response = await axios.get(`${baseUrl}?row=${row}&page=${options.page}&start_date=<${startDate}`)
    if (!response.data.Success || !response.data.Data || !response.data.Data.features) {
      return null
    }
    // 終了日付設定なし対応
    response.data.Data.features = response.data.Data.features.filter((feature: any) => {
      return feature.properties.end_date == null || feature.properties.end_date >= startDate
    })
    // アイコン設定
    response.data.Data.features.forEach((feature: any) => {
      if (feature.properties.deregulation_flag === '1') {
        // 規制解除中
        feature.properties.icon = `${sourceId}_rgkaijo`
      } else {
        const finded = RoadSymbols.find(row => row.name === feature.properties.regulation_type)
        if (finded) {
          feature.properties.icon = `${sourceId}_${finded.id}`
        } else {
          feature.properties.icon = `${sourceId}_other`
        }
      }
    })
    return response.data
  }

  const fetchFeatures = async () => {
    const data = await fetchData({page: 1})
    if (!data) return
    const totalPage = data.TotalPage
    let arr: any[] = data.Data.features
    for (let page = 2; page <= totalPage; page++) {
      const data = await fetchData({page: page})
      if (!data) {
        break
      }
      arr = arr.concat(data.Data.features)
    }
    setFeatures(arr)
  }

  const getFilterFeatureCollection = (): GeoJSON.FeatureCollection => {
    const targetTypes = state.app.roadRegulationTypes
    const filterFeatures = features.filter(feature => {
      const category: string = feature.properties?.category
      if (category.startsWith('事前') && targetTypes.includes(RoadType.TENKI)) return true
      if (category.startsWith('災害') && targetTypes.includes(RoadType.TENKI)) return true
      if (category === '気象条件' && targetTypes.includes(RoadType.TENKI)) return true
      if (category === '工事' && targetTypes.includes(RoadType.KOUJI)) return true
      if (category.startsWith('予告') && targetTypes.includes(RoadType.YOKOKU)) return true
      if (category === 'その他' && targetTypes.includes(RoadType.ETC)) return true
      if (category === 'イベント' && targetTypes.includes(RoadType.ETC)) return true
      return false
    })
    return {
      type: 'FeatureCollection',
      features:filterFeatures
    }
  }

  const load = async () => {
    if (!state.mapbox) return

    // symbol images
    RoadSymbols.forEach(row => {
      state.map.loadImage(`${sourceId}_${row.id}`, row.url, false)
    })

    // source
    state.map.addSource(sourceId, {
      type: 'geojson',
      cluster: false,
      clusterMaxZoom: 1,
      clusterRadius: 1,
      data: getFilterFeatureCollection()
    })

    // outline
    state.map.addLayer({
      id: outlineLayoutId,
      type: 'line',
      source: sourceId,
      layout: {
        'line-join': 'round',
        'line-cap': 'round'
      },
      paint: {
        'line-color': 'black',
        'line-width': 5
      }
    })

    // line
    state.map.addLayer({
      id: lineLayoutId,
      type: 'line',
      source: sourceId,
      layout: {
        'line-join': 'round',
        'line-cap': 'round'
      },
      paint: {
        'line-color': [
          'match',
          ['get', 'category'],
          roadColors.Saigai.category, roadColors.Saigai.color,
          roadColors.Kouji.category, roadColors.Kouji.color,
          roadColors.Etc.color
        ],
        'line-width': 3
      }
    })

    // symbol
    state.map.addLayer({
      id: symbolLayoutId,
      type: 'symbol',
      source: sourceId,
      layout: {
        'symbol-placement': 'line-center',
        'icon-image': ['get', 'icon'],
        'icon-size': 1,
        'icon-rotation-alignment': 'viewport',
        'icon-allow-overlap': true,
        'text-allow-overlap': false,
        'text-optional': true,
      }
    })

    setLayoutvisibility()
  }

  return (
    <></>
  )
}
